(() => {
  const mobileQuery = matchMedia('(max-width: 767px)');
  let vehicleDataPromise;

  class ChangeCarModal {
    constructor(root) {
      this.root = root;
      this.dataset = root.dataset;
      this.trigger = root.querySelector('[data-change-car-trigger]');
      this.dialog = root.querySelector('[data-change-car-dialog]');
      this.closeButton = root.querySelector('[data-change-car-close]');
      this.makeSelect = root.querySelector('[data-change-car-make]');
      this.modelSelect = root.querySelector('[data-change-car-model]');
      this.submitButton = root.querySelector('[data-change-car-submit]');
      this.status = root.querySelector('[data-change-car-status]');
      this.error = root.querySelector('[data-change-car-error]');
      this.vehicleData = [];

      if (!this.trigger || !this.dialog) return;
      this.bindEvents();
    }

    bindEvents() {
      this.trigger.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => this.dialog.close());
      this.makeSelect.addEventListener('change', () => this.updateModels());
      this.modelSelect.addEventListener('change', () => {
        this.updateSubmitState();
        if (this.modelSelect.value) {
          requestAnimationFrame(() => this.modelSelect.blur());
        }
      });
      this.submitButton.addEventListener('click', () => this.submit());
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('change-car-modal-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog.close();
        }
      }, { capture: true });
    }

    async open() {
      if (!mobileQuery.matches) return;
      if (typeof this.dialog.showModal !== 'function') {
        window.location.assign('/');
        return;
      }

      this.trigger.setAttribute('aria-expanded', 'true');
      this.dialog.showModal();
      document.documentElement.classList.add('change-car-modal-open');
      this.setLoading(true);

      try {
        this.vehicleData = await this.loadVehicleData();
        this.populateMakes();
        this.setLoading(false);
        requestAnimationFrame(() => {
          this.makeSelect.focus({ preventScroll: true });
        });
      } catch (error) {
        this.setLoading(false);
        this.error.hidden = false;
        this.status.textContent = '';
      }
    }

    loadVehicleData() {
      if (Array.isArray(window.WiperProVehicleData)) {
        return Promise.resolve(window.WiperProVehicleData);
      }
      if (vehicleDataPromise) return vehicleDataPromise;

      vehicleDataPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.dataset.vehicleDataUrl;
        script.async = true;
        script.dataset.vehicleDataLoader = 'true';
        script.addEventListener('load', () => {
          if (Array.isArray(window.WiperProVehicleData)) {
            resolve(window.WiperProVehicleData);
          } else {
            vehicleDataPromise = undefined;
            reject(new Error('Vehicle data asset did not initialise'));
          }
        });
        script.addEventListener('error', () => {
          vehicleDataPromise = undefined;
          reject(new Error('Vehicle data asset failed to load'));
        });
        document.head.appendChild(script);
      });
      return vehicleDataPromise;
    }

    populateMakes() {
      const makes = [...new Set(this.vehicleData.map((vehicle) => vehicle.make))]
        .sort((a, b) => a.localeCompare(b));
      this.makeSelect.replaceChildren(new Option('Select Make', ''));
      makes.forEach((make) => this.makeSelect.add(new Option(make, make)));
      this.makeSelect.disabled = false;
    }

    updateModels() {
      const make = this.makeSelect.value;
      const models = [...new Set(
        this.vehicleData.filter((vehicle) => vehicle.make === make).map((vehicle) => vehicle.model)
      )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      this.modelSelect.replaceChildren(new Option('Select model', ''));
      models.forEach((model) => this.modelSelect.add(new Option(model, model)));
      this.modelSelect.disabled = models.length === 0;
      this.updateSubmitState();

      if (make && !this.modelSelect.disabled) {
        requestAnimationFrame(() => {
          this.modelSelect.focus({ preventScroll: true });
        });
      }
    }

    updateSubmitState() {
      this.submitButton.disabled = !(this.makeSelect.value && this.modelSelect.value);
    }

    submit() {
      const found = this.vehicleData.find(
        (vehicle) => vehicle.make === this.makeSelect.value && vehicle.model === this.modelSelect.value
      );
      if (!found) {
        this.error.hidden = false;
        this.error.textContent = 'That vehicle could not be found. Please choose another model or contact us.';
        return;
      }
      window.location.assign(`${this.dataset.productsRoot}${encodeURIComponent(found.slug)}`);
    }

    setLoading(loading) {
      this.error.hidden = true;
      if (loading) {
        this.makeSelect.disabled = true;
        this.modelSelect.disabled = true;
        this.submitButton.disabled = true;
        this.status.textContent = 'Loading vehicle options…';
        return;
      }

      this.makeSelect.disabled = false;
      this.modelSelect.disabled = !this.makeSelect.value || this.modelSelect.options.length <= 1;
      this.updateSubmitState();
      this.status.textContent = '';
    }

  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-change-car-modal]').forEach((root) => {
      if (root.dataset.changeCarReady === 'true') return;
      root.dataset.changeCarReady = 'true';
      new ChangeCarModal(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
